/**
 * PWA: instalacao contextual (RF-UX-033/071..075). Chromium expoe
 * `beforeinstallprompt`; iOS NAO tem prompt programatico (So tutorial).
 * O prompt so aparece apos uso real e e dispensavel por 30 dias.
 */
import { useEffect, useState } from "react";
import { registerSW } from "virtual:pwa-register";
import { toast } from "./store";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "zapir.install.dismissedUntil";
const VISITS_KEY = "zapir.install.visits";
export const INSTALL_DISMISS_MS = 30 * 24 * 60 * 60 * 1000;

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

export function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !/crios|fxios/i.test(navigator.userAgent);
}

function registerVisit(): number {
  const visits = Number(localStorage.getItem(VISITS_KEY) ?? "0") + 1;
  localStorage.setItem(VISITS_KEY, String(visits));
  return visits;
}

export function useInstallPrompt(): {
  canInstall: boolean;
  needsIOSInstructions: boolean;
  promptInstall: () => Promise<void>;
  dismiss: () => void;
} {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(() => {
    const until = Number(localStorage.getItem(DISMISS_KEY) ?? "0");
    return Date.now() < until;
  });

  useEffect(() => {
    registerVisit();
    const onPrompt = (event: Event) => {
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const promptInstall = async () => {
    if (!deferred) return;
    await deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === "dismissed") {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + INSTALL_DISMISS_MS));
    }
    setDeferred(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + INSTALL_DISMISS_MS));
    setDismissed(true);
  };

  const canInstall = Boolean(deferred) && !dismissed && !isStandalone();
  return { canInstall, needsIOSInstructions: isIOS() && isStandalone() === false, promptInstall, dismiss };
}

/** Registra o service worker e oferece update nao destrutivo (EC-UX-018). */
export function registerServiceWorker(): void {
  if (import.meta.env.DEV) return;
  registerSW({
    immediate: true,
    onNeedRefresh() {
      toast({
        tone: "info",
        message: "Uma nova versão do Zapir está disponível.",
        actionLabel: "Atualizar",
        onAction: () => window.location.reload(),
      });
    },
  });
}
